import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

import {
  createMarketplaceProfileTestimonial,
  MarketplaceTestimonialsKeys,
} from "../../api/marketplace-testimonials";
import { CreateMarketplaceProfileTestimonialPayload } from "../../types/marketplace-testimonials";

const testimonialFormSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .max(100, "Name must be less than 100 characters"),
  company: z
    .string()
    .min(1, "Company is required")
    .max(100, "Company must be less than 100 characters"),
  content: z
    .string()
    .min(1, "Testimonial content is required")
    .max(1000, "Testimonial must be less than 1000 characters"),
});

type TestimonialFormValues = z.infer<typeof testimonialFormSchema>;

interface AddTestimonialDialogProps {
  profileId: string;
  children?: React.ReactNode;
}

const AddTestimonialDialog = ({
  profileId,
  children,
}: AddTestimonialDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<TestimonialFormValues>({
    resolver: zodResolver(testimonialFormSchema),
    defaultValues: {
      name: "",
      company: "",
      content: "",
    },
  });

  const { mutate: createTestimonial, isPending } = useMutation({
    mutationFn: (data: CreateMarketplaceProfileTestimonialPayload) =>
      createMarketplaceProfileTestimonial(profileId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [MarketplaceTestimonialsKeys.GET_TESTIMONIALS, profileId],
      });
      toast({
        title: "Testimonial added successfully!",
        description: "Your testimonial has been added to your profile.",
      });
      form.reset();
      setIsOpen(false);
    },
    onError: () => {
      toast({
        title: "Failed to add testimonial",
        description: "Please try again later.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: TestimonialFormValues) => {
    createTestimonial(data as CreateMarketplaceProfileTestimonialPayload);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      form.reset();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        {children || (
          <Button variant="outline" size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Testimonial
          </Button>
        )}
      </SheetTrigger>
      <SheetContent
        className="w-[90%] max-w-[90%] h-full bg-white p-0 flex flex-col rounded-l-2xl sm:w-full sm:max-w-[600px] sm:rounded-none"
        side="right"
      >
        <SheetHeader className="border-b border-border flex-none py-6 px-4 sm:px-6 text-left">
          <SheetTitle className="text-xl font-semibold text-foreground">
            Add Testimonial
          </SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground">
            Add a testimonial from a satisfied client to showcase on your
            profile.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-y-auto py-6 px-4 sm:px-6">
          <Form {...form}>
            <form
              id="testimonial-form"
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-6"
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John Smith" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Company</FormLabel>
                    <FormControl>
                      <Input placeholder="Acme Inc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Testimonial</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Share what the client said about working with you..."
                        className="min-h-[100px] resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>

        <div className="border-t border-border bg-white flex-none py-6 px-4 sm:px-6">
          <div className="flex items-center gap-4">
            <Button type="submit" form="testimonial-form" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Testimonial
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AddTestimonialDialog;
